import { computed, defineComponent, h, ref } from 'vue'
// Components
import { QAvatar, QField, QIcon, QImg, QInnerLoading, QSpinnerGears, QUploader } from 'quasar'
import { BsBtn } from '../index'
import { bsConstants, validStr } from 'booksprout'

import { BsComponentRef, bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { ComponentState } from '../useBsComponent'
import { useUi } from '../../../modules/ui/uiModule'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'
import { emitModel } from '../emitModel'

const Papa = require('papaparse')

// What nginx will accept. Past this the request is refused at the edge, so the upload is stopped here
// instead - see UPLOADS.MAX_SIZE_MB. [sc-27747]
const MAX_UPLOAD_BYTES = bsConstants.UPLOADS.MAX_SIZE_MB * 1024 * 1024

// The data URL read below holds the file in memory again as base64, so it is capped far below the
// upload limit. This is NOT a limit on uploading: a file over it still uploads, it just never becomes
// a data URL, which only the `img` and `input` types read. Book files are well past it routinely and
// are meant to be.
const MAX_DATA_URL_BYTES = 15 * 1024 * 1024

/**
 * The uploader, in three shapes: an avatar-sized image picker (`img`), a one-line file field (`input`),
 * and the drag-and-drop panel (`file`). It wraps QUploader, which does the transfer.
 *
 * Refusing a file means resetting AND aborting the uploader: by the time this sees one QUploader has
 * queued it, and with `autoUpload` it is already on its way.
 *
 * `reset` is its own — it clears the queue and the file name rather than restoring a field value — so
 * the composable's is deliberately left out of the setup return.
 */
/**
 * A `$refs` entry holding a BsUpload: `BsComponentRef` plus what the two book/arc screens call on one.
 * `doAutoSave` and `errorMessage`/`componentState` come from the shared type.
 */
export type BsUploadRef = BsComponentRef & {
  upload: () => void
  doAutoSave: () => void
}

export default defineComponent({
  name: 'BsUpload',

  // See BsInput: undeclared DOM-named events are bound twice, once natively — `input` and `click` both
  // qualify. The rest are this component's own upload lifecycle.
  emits: [
    'input', 'update:modelValue', 'click', 'done', 'failed', 'finish', 'parsed', 'remove', 'uploaded'
  ],

  props: {
    ...bsComponentProps,
    value: { type: String, default: void 0 },
    canReset: { type: Boolean, default: true },
    autoUpload: { type: Boolean, default: true },
    type: { type: String as unknown as () => 'img' | 'input' | 'file' | undefined, default: void 0 },
    label: { type: String, default: void 0 },
    width: { type: Number, default: void 0 },
    height: { type: Number, default: void 0 },
    /**
     * These two are deliberately WITHOUT a default, as the class declared them: Vue's own casting makes
     * an absent Boolean false, and `square` is passed straight to QAvatar's `square` prop — with
     * `default: void 0` an avatar that should be round gets `undefined` instead of `false`. See the
     * longer note on BsBtn's `active`.
     */
    square: { type: Boolean },
    hidePreview: { type: Boolean },
    hintText: { type: String, default: void 0 },
    parseCsv: { type: Boolean, default: false },
    csvNoHeader: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: bsUpload, set: setBsUploadRef } = useTemplateRef<any>()

    const vm = useVm()

    const hasFile = ref(false)
    const forceAllowDragAndDrop = ref(false)
    const fileName = ref('')
    const isUploading = ref(false)
    // Built once from the prop, as the class's field initialiser was — it is not re-read on a change.
    const uploaderHtml = ref(`
    <div><strong>Drag & Drop</strong> <br /> ${props.hintText || 'a image file here'}</div>
    <strong>OR</strong>
  `)

    // `reset` is left out of the spread: this one empties the uploader rather than restoring a value.
    const { reset, ...bs } = useBsComponent({
      vm,
      props,
      emit,
      value: () => props.value,
      // A queued file counts as filled even before it has uploaded, when the caller uploads by hand.
      isComponentValid: (val?: any) =>
        (val !== '' && val !== void 0 && val !== null) || (props.autoUpload === false && validStr(fileName.value))
    })
    void reset

    /**
     * The modelled value is `componentValue`, not the `value` prop, everywhere below: every caller binds
     * this with `v-model`, which Vue 3 passes as `modelValue`, so `value` is undefined for all of them.
     * Reading it showed the awaiting-image placeholder and the drag-and-drop box in place of a cover that
     * was there all along — stored, and drawn by the page's own preview beside it (sc-28864).
     */
    const validValue = computed(() => {
      const value = bs.componentValue.value

      return value !== '' && value !== void 0 && value !== null
    })

    return {
      bsUpload,
      setBsUploadRef,
      ...bs,
      hasFile,
      forceAllowDragAndDrop,
      fileName,
      isUploading,
      uploaderHtml,
      validValue
    }
  },

  methods: {
    /**
     * The uploader may already be gone, and on Vue 3 that is the ordinary case rather than the odd one.
     *
     * QUploader aborts any transfer still running when it unmounts, the abort fires `failed`, and
     * `failed` calls this. Vue 3 clears a template ref BEFORE it runs the child's beforeUnmount hooks,
     * where Vue 2 cleared it after -- so on Vue 2 `reset()` still had an uploader to talk to and here
     * it has null. Leaving a step of the pen-name wizard while the profile photo was still uploading
     * threw "Cannot read properties of null (reading 'reset')" out of a listener, which is uncaught and
     * takes the page with it.
     *
     * There is nothing to reset or abort on a component that has already torn both down; the state this
     * method owns still needs clearing.
     */
    reset () {
      const uploader = (this.bsUpload as QUploader | null)
      uploader?.reset()
      uploader?.abort()
      this.fileName = ''
      this.hasFile = false
      this.isUploading = false
      emitModel(this, '')
    },

    /**
     * Stop the upload and put the message where the author will see it.
     *
     * QUploader has already queued the file by the time we get a look at it, and with autoUpload it is
     * on its way, so refusing one means resetting and aborting rather than just declining to proceed.
     *
     * Renamed from `__rejectFile`: Vue does not proxy setup/method keys beginning with `_`.
     * @param errorCode key under system.errors
     * @param params interpolated into that message
     */
    rejectFile (errorCode: string, params?: Record<string, unknown>) {
      // Optional for the same reason as `reset` above: this runs from a QUploader event too.
      const uploader = (this.bsUpload as QUploader | null)
      uploader?.reset()
      uploader?.abort()
      this.fileName = ''
      this.hasFile = false
      this.isUploading = false
      this.forceAllowDragAndDrop = true

      this.componentState = ComponentState.Invalid
      this.errorMessage = this.$tc('system.errors.' + errorCode, void 0, params)
    },

    filesAdded (files: any) {
      this.isUploading = true
      this.$emit('begin-processing', files)
      if (files.length) {
        const
          fileReader = new FileReader(),
          file = files[0]

        this.hasFile = this.autoUpload !== true || this.type === 'input'
        this.fileName = file.name

        // nginx refuses anything over this with a 413 whose body is an HTML error page, so the `failed`
        // handler below cannot parse an errorCode out of it and the author was told only "File upload
        // failed. Please try again." after waiting on a transfer that was never going to be accepted.
        // Refuse it here, before the request starts, and name the limit. [sc-27747]
        if (file.size > MAX_UPLOAD_BYTES) {
          this.$q.loading.hide()
          this.$emit('error-processing', 'file_size')
          this.$emit('failed', this.$tc('system.errors.fileTooLarge', void 0, { size: bsConstants.UPLOADS.MAX_SIZE_MB }))
          this.rejectFile('fileTooLarge', { size: bsConstants.UPLOADS.MAX_SIZE_MB })
          return
        }

        if (file.size > MAX_DATA_URL_BYTES) {
          this.$q.loading.hide()
          this.$emit('error-processing', 'file_size')
        } else {
          // Get the image the user has uploaded as a base64 encoded string and emit it (return the value)
          fileReader.onload = (fileLoadedEvent: any) => {
            this.$emit('done-processing', files)
            // this.isUploading = false
            if (this.type !== 'input') {
              const srcData = fileLoadedEvent.target.result

              if (this.parseCsv) {
                this.$emit('parsed', Papa.parse(srcData, {
                  header: !this.csvNoHeader,
                  skipEmptyLines: true
                }))
              }
              emitModel(this, srcData)
            }
          }

          if (this.type !== 'input' && !this.parseCsv) {
            fileReader.readAsDataURL(file)
          } else {
            fileReader.readAsText(file)
          }
        }
      }
      this.$emit('done-processing', files)
    },

    renderInputUpload () {
      return [
        h(QIcon, {
          name: this.hasFile ? 'icon-check' : 'icon-options'
        }),
        h('div', {
          class: 'bs-upload__file col',
          /**
           * Quasar 2's `pickFiles` takes the event so it can tell a real pointer click from a
           * synthetic one. It handles a missing one (anything not an object becomes
           * `{ target: null }`), which is what a `$emit('click')` with no payload sends.
           */
          onClick: (e?: MouseEvent) => (this.bsUpload as QUploader).pickFiles(e as MouseEvent)
        }, [
          this.fileName || this.$attrs.placeholder,
          this.renderDeleteFileButton()
        ]),
        h(BsBtn, {
          class: 'icon-bs-g',
          icon: 'app:browse',
          iconOnly: true,
          onClick: (e?: MouseEvent) => (this.bsUpload as QUploader).pickFiles(e as MouseEvent)
        })
      ]
    },

    renderDeleteFileButton () {
      if (this.hasFile === false) return void 0

      return h(QIcon, {
        class: 'on-right',
        name: 'cancel',
        onClick: (e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            this.reset()
            this.$emit('remove')
          }
      })
    },

    renderPreview () {
      if (this.hidePreview) {
        return
      }

      return h('div', {
        class: ['bs-upload__before', [{
          'bs-upload__before--waiting': !this.hasFile,
          'bs-upload__before--has-file': this.hasFile,
          'bs-upload__before--has-image': this.validValue && !this.hasFile
        }]]
      }, [
        h(QAvatar, {
          style: {
            height: this.square ? this.height + 'px' : void 0,
            width: this.square ? this.width + 'px' : void 0
          },
          square: this.type === 'img' || this.square
        }, { default: () => [
          h(QImg, {
            src: this.componentValue || '/cdn/awaiting-image.png'
          })
        ] })
      ])
    },

    renderDragAndDropUpload () {
      return h('div', {
        class: 'bs-upload__drag-drop-container'
      }, [
        h('div', {
          class: 'bs-upload__hint',
          innerHTML: this.type === 'img' ? void 0 : this.uploaderHtml
        }),
        h(BsBtn, {
          secondary: true,
          label: this.label || 'Browse',
          onClick: (e?: MouseEvent) => (this.bsUpload as QUploader).pickFiles(e as MouseEvent)
        })
      ])
    },

    renderResetFile () {
      if (!this.canReset) {
        return void 0
      }

      let label = ''
      if (this.type !== 'img') {
        label = 'Remove Image'
      }

      if (this.type === 'file') {
        label = 'Remove File'
      }

      return h('div', {
        class: 'bs-upload__existing-container'
      }, [
        h(BsBtn, {
          secondary: true,
          label: label,
          onClick: () => {
              this.reset()
              this.$emit('remove')
            }
        }),
        this.type !== 'img' ? void 0 : h(BsBtn, {
          flat: true,
          icon: 'icon-check',
          onClick: () => {
              this.$emit('done', this.componentValue)
              this.hasFile = false
            }
        })
      ])
    },

    renderScopedSlots () {
      const node: any[] = []

      if (this.type === 'input') {
        node.push(this.renderInputUpload())
      } else {
        node.push(this.renderPreview())
        if (this.type !== 'img' || !this.validValue || this.hasFile) {
          if (!this.forceAllowDragAndDrop && (this.hasFile || this.validValue)) { // We have a temp file we can upload
            node.push(this.renderResetFile())
          } else if (this.hasFile === false && (this.autoUpload === void 0 || !this.validValue || this.forceAllowDragAndDrop)) {
            node.push(this.renderDragAndDropUpload())
          }
        }
      }

      const slots = {
        list: () => {
          return h('div', {
            class: 'bs-upload__content'
          }, [node])
        }
      }

      return slots
    },

    upload () {
      return (this.bsUpload as QUploader).upload()
    },

    renderProgress () {
      return h(QInnerLoading, {
        showing: this.isUploading
      }, { default: () => [
        h(QSpinnerGears, {
          color: 'primary',
          size: '30px'
        })
      ] })
    }
  },

  render () {
    // If  value has been passed in, set it.
    if (this.componentValue) {
      this.hasFile = this.type === 'input'
      this.fileName = this.componentValue
    }

    const props = {
      flat: true,
      noThumbnails: true,
      color: 'transparent',
      multiple: false,
      autoUpload: this.autoUpload
    }

    const scopedSlots = {
      ...this.renderScopedSlots()
    }

    const listeners = {
      added: this.filesAdded,
      /**
       * `factoryFailed`, not `factory-failed`: Quasar 2 renamed the event.
       *
       * Vue 3's `emit` looks for `onFactoryFailed` and falls back to the hyphenated `onFactory-failed`
       * ONLY for a model listener, so the old name was simply never called -- and this is the listener
       * that reports a rejected upload. A book cover under 415px tall was refused by the factory and
       * the author was told nothing at all: the uploader emptied itself and no message appeared.
       */
      factoryFailed: (errorCode: string) => {
        this.rejectFile(errorCode)
      },
      failed: (info: any) => {
        /**
         * An abort we caused ourselves is not a failure to report.
         *
         * QUploader aborts whatever is still in flight when it unmounts, and the abort comes back
         * here as `failed`. Leaving a wizard step mid-upload therefore ran the whole error path
         * during teardown: an error dialog mounted while this tree was unmounting, and the page died
         * with "Cannot read properties of undefined (reading '__disposer')" -- vue-i18n's unmounted
         * hook, run a second time on an instance it had already cleaned up.
         *
         * A null ref is how that state is recognised: Vue 3 clears a template ref before it runs the
         * child's beforeUnmount hooks, so by the time QUploader aborts, `bsUpload` is already gone.
         */
        if (this.bsUpload === null || this.bsUpload === void 0) {
          return
        }

        this.isUploading = false
        let response

        try {
          // A failed upload with no body (abort / network drop / gateway timeout) has an empty
          // xhr.response - expected, not a JSON bug, so don't parse or log it. Only a non-empty
          // body that isn't JSON is a real anomaly worth surfacing to Sentry via console.error.
          response = info.xhr?.response ? JSON.parse(info.xhr.response) : {}
        } catch (e) {
          console.error('failed to parse json', e, info.xhr.response)
          response = {}
        }

        const message = response.message?.errorCode
          ? this.$tc('system.errors.' + response.message.errorCode)
          : response.message || this.$tc(`system.errors.${this.type === 'file' ? 'fileUploadFailed' : 'imageUploadFailed'}`)

        this.$emit('failed', message)

        void useUi().ui.showMessageDialog({
          error: true,
          message
        })

        this.reset()
      },
      uploaded: (data: any) => {
        this.$emit('uploaded', data)
      },
      finish: () => {
        this.isUploading = false
        this.$emit('finish')
      }
    }

    const
      isInput = this.type === 'input',
      isImg = this.type === 'img',
      isFile = this.type === 'file'


    return h('div', {
      class: ['bs-upload', this.getClasses({
        'bs-upload--input': isInput,
        'bs-upload--img': isImg,
        'bs-upload--inline': !isInput && !isImg,
        'bs-upload--no-file': !this.hasFile,
        'bs-upload--has-file': this.hasFile,
        'bs-upload--square': this.square,
        'bs-upload--is-uploading': this.isUploading
      })],
      style: {
        height: isImg || isFile ? this.height + 'px' : void 0,
        width: isImg || isFile ? this.width + 'px' : void 0
      },
      // Was `$vnode.data.model.expression`, the compiler's record of the v-model binding, which Vue 3
      // does not keep. The `dataModel` prop is the explicit replacement -- see bsComponentProps.
      ['data-model']: this.dataModel
    }, [
      h(QField, {
        class: 'bs-upload-q-field full-width',
        ...this.getValidationProps({
          borderless: true,
          bottomSlots: true,
          noErrorIcon: true,
          dense: true,
          stackLabel: true
        })
      }, {
          error: () => this.renderErrorSlot(),
          ...this.renderAutoSaveMessage(),
        default: () => [
        h(QUploader, {
          class: 'full-width',
          ref: this.setBsUploadRef,
          ...this.getRenderProps(props),
          ...quasarHandlers(this.getListeners(listeners)),
          ...this.getAttributes()
        }, this.getSlots(scopedSlots)),
        this.isUploading ? this.renderProgress() : void 0
      ]
      })
    ])
  }
})
